import { ref } from 'vue'
import { productService } from '@/services/productService'
import type { ApiProduct, CreateProductDto, UpdateProductDto } from '@/types/api-product'

/**
 * Composable para manejar productos
 */
export function useProducts() {
  const products = ref<ApiProduct[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)

  /**
   * Cargar todos los productos
   */
  const fetchProducts = async () => {
    loading.value = true
    error.value = null
    try {
      products.value = await productService.getAll()
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Error loading products'
    } finally {
      loading.value = false
    }
  }

  /**
   * Obtener un producto por ID
   */
  const getProductById = async (id: number) => {
    loading.value = true
    error.value = null
    try {
      const product = await productService.getById(id)
      return product
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Error loading product'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Crear un nuevo producto
   */
  const createProduct = async (productData: CreateProductDto) => {
    loading.value = true
    error.value = null
    try {
      const newProduct = await productService.create(productData)
      products.value.push(newProduct)
      return newProduct
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Error creating product'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Actualizar un producto
   */
  const updateProduct = async (id: number, productData: UpdateProductDto) => {
    loading.value = true
    error.value = null
    try {
      const updatedProduct = await productService.update(id, productData)
      const index = products.value.findIndex((p) => p.Product_id === id)
      if (index !== -1) {
        products.value[index] = updatedProduct
      }
      return updatedProduct
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Error updating product'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Eliminar un producto
   */
  const deleteProduct = async (id: number) => {
    loading.value = true
    error.value = null
    try {
      await productService.delete(id)
      products.value = products.value.filter((p) => p.Product_id !== id)
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Error deleting product'
      throw err
    } finally {
      loading.value = false
    }
  }

  return {
    products,
    loading,
    error,
    fetchProducts,
    getProductById,
    createProduct,
    updateProduct,
    deleteProduct,
  }
}

